import { elakayAutoText } from "../dataElakay";

/**
 * ContentPageTemplates.js — 16 templates PageBuilder pour le mode "contentPage".
 *
 * Source de donnees : contentPage
 *   contentPage.pageTitle            -> titre de la page
 *   contentPage.sections[]           -> tableau de sections
 *     section.title                  -> titre de section
 *     section.text.0                 -> premier paragraphe
 *     section.text.1                 -> deuxieme paragraphe
 *     section.list.0                 -> premier item de liste
 *     section.imagePath              -> URL image
 *     section.videoPath              -> URL video
 *     section.href.title             -> label lien externe
 *     section.href.href              -> URL lien externe
 */

let _c = 0;
const id = () => `cp-${(++_c).toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const PAGE_STYLE = { width: "100%", margin: 0, padding: 0, fontFamily: "'DM Sans', system-ui, sans-serif" };

const rSections = (mode = "children") => ({
  source: "contentPage",
  path: "sections",
  mode,
  autoFill: false,
  fieldMap: {},
});

const makePage = (name, children, extraMeta = {}) => {
  const p = id();
  return {
    meta: { title: name, description: `Template ${name}`, ...extraMeta },
    pageMode: "contentPage",
    currentPageId: p,
    pages: [{ id: p, name, style: PAGE_STYLE, children }],
  };
};

const heading = (text, bindKey, tag, style = {}) => ({
  id: id(),
  type: "heading",
  text,
  bindKey,
  tag,
  style: { margin: 0, lineHeight: 1.1, ...style },
});

const textNode = (text, bindKey, tag = "p", style = {}) => ({
  id: id(),
  type: "text",
  text,
  bindKey,
  tag,
  style: { margin: 0, lineHeight: 1.7, ...style },
});

const imageNode = (src, style = {}, bindKey = "imagePath") => ({
  id: id(),
  type: "image",
  src,
  ...(bindKey ? { bindKey } : {}),
  alt: "",
  style: { width: "100%", objectFit: "cover", display: "block", ...style },
});

const videoNode = (style = {}, bindKey = "videoPath") => ({
  id: id(),
  type: "video",
  videoUrl: "",
  options: { url: "", ratio: "16 / 9" },
  ...(bindKey ? { bindKey } : {}),
  style: {
    width: "100%",
    height: "300px",
    border: "0",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#000000",
    display: "block",
    ...style,
  },
});

const divider = (style = {}) => ({
  id: id(),
  type: "divider",
  style: { border: "none", borderTop: "1px solid #e5e7eb", width: "100%", margin: 0, ...style },
});

const stack = (children, style = {}) => ({
  id: id(),
  type: "stack",
  style: { display: "flex", flexDirection: "column", gap: "14px", ...style },
  children,
});

const row = (children, style = {}) => ({
  id: id(),
  type: "row",
  style: { display: "flex", gap: "24px", flexWrap: "wrap", ...style },
  children,
});

const panel = (children, style = {}, repeatBind = null) => ({
  id: id(),
  type: "panel",
  ...(repeatBind ? { repeatBind } : {}),
  style: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "28px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    ...style,
  },
  children,
});

const section = (children, style = {}) => ({
  id: id(),
  type: "section",
  style: { width: "100%", padding: "56px 48px", display: "flex", flexDirection: "column", gap: "24px", ...style },
  children,
});

const repeatedArticleCard = (theme = {}) => panel([
  heading(elakayAutoText("h_fc6035c21742d4", "Titre de section"), "title", "h2", {
    fontSize: theme.titleSize || "22px",
    fontWeight: 850,
    color: theme.title || "#111827",
  }),
  textNode(elakayAutoText("h_f10edafdee5eaa", "Contenu de la section..."), "text.0", "p", {
    fontSize: theme.textSize || "15px",
    color: theme.text || "#4b5563",
  }),
  textNode("", "text.1", "p", {
    fontSize: "14px",
    color: theme.textMuted || theme.text || "#6b7280",
  }),
  imageNode(theme.image || "https://placehold.co/760x320/f3f4f6/9ca3af?text=Image", {
    maxHeight: theme.imageHeight || "300px",
    borderRadius: theme.imageRadius || "6px",
  }),
  videoNode({
    height: theme.videoHeight || theme.imageHeight || "300px",
    borderRadius: theme.imageRadius || "6px",
  }),
], {
  background: theme.bg || "#ffffff",
  border: `1px solid ${theme.border || "#e5e7eb"}`,
  boxShadow: theme.shadow || "0 2px 10px rgba(15,23,42,0.06)",
  padding: theme.pad || "30px",
}, rSections());

const repeatedSplit = (theme = {}) => ({
  id: id(),
  type: "row",
  repeatBind: rSections(),
  style: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.gap || "32px",
    padding: theme.pad || "34px 0",
    borderBottom: `1px solid ${theme.border || "#e5e7eb"}`,
    flexWrap: "wrap",
  },
  children: [
    imageNode(theme.image || "https://placehold.co/520x340/e5e7eb/64748b?text=Image", {
      width: theme.imageWidth || "42%",
      minWidth: "260px",
      maxHeight: theme.imageHeight || "340px",
      borderRadius: theme.imageRadius || "8px",
      flex: "1 1 300px",
    }),
    videoNode({
      width: theme.imageWidth || "42%",
      minWidth: "260px",
      height: theme.videoHeight || theme.imageHeight || "340px",
      borderRadius: theme.imageRadius || "8px",
      flex: "1 1 300px",
    }),
    stack([
      heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", {
        fontSize: theme.titleSize || "25px",
        fontWeight: 850,
        color: theme.title || "#111827",
      }),
      textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", {
        fontSize: "15px",
        color: theme.text || "#475569",
      }),
      textNode("", "text.1", "p", {
        fontSize: "14px",
        color: theme.textMuted || theme.text || "#64748b",
      }),
    ], {
      flex: "1 1 320px",
      justifyContent: "center",
      padding: theme.textPad || "8px 0",
    }),
  ],
});

const repeatedStatCard = (theme = {}) => panel([
  textNode("01", null, "p", {
    fontSize: "12px",
    fontWeight: 800,
    color: theme.accent || "#2563eb",
    letterSpacing: "0.16em",
  }),
  heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h3", {
    fontSize: "20px",
    fontWeight: 850,
    color: theme.title || "#111827",
  }),
  textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", {
    fontSize: "14px",
    color: theme.text || "#475569",
  }),
  imageNode(theme.image || "https://placehold.co/520x260/e5e7eb/64748b?text=Image", {
    height: theme.imageHeight || "170px",
    borderRadius: theme.imageRadius || "6px",
  }),
  videoNode({
    height: theme.videoHeight || theme.imageHeight || "190px",
    borderRadius: theme.imageRadius || "6px",
  }),
], {
  background: theme.bg || "#ffffff",
  border: `1px solid ${theme.border || "#e5e7eb"}`,
  boxShadow: theme.shadow || "none",
}, rSections());

/* =============================================================================
   1 — Classique editorial
   ========================================================================== */
export function makeContentTemplateClassique() {
  const C = { ink: "#111827", muted: "#4b5563", bg: "#f8fafc", paper: "#ffffff", accent: "#6d28d9", border: "#e5e7eb" };
  return makePage("Contenu Classique", [
    section([
      textNode(elakayAutoText("h_ba22b74fbeddc1", "PAGE DE CONTENU"), null, "p", { fontSize: "11px", fontWeight: 800, color: C.accent, letterSpacing: "0.2em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "42px", fontWeight: 900, color: C.ink }),
      divider({ borderTop: `3px solid ${C.accent}`, width: "64px" }),
    ], { background: C.paper, borderBottom: `1px solid ${C.border}` }),
    section([repeatedArticleCard({ title: C.ink, text: C.muted, bg: C.paper, border: C.border })], { background: C.bg, paddingTop: "40px" }),
  ]);
}

/* =============================================================================
   2 — Hero sombre
   ========================================================================== */
export function makeContentTemplateHero() {
  const C = { navy: "#0f172a", orange: "#f97316", bg: "#f8fafc", white: "#ffffff", gray: "#64748b", border: "#e2e8f0" };
  return makePage("Contenu Hero", [
    section([
      textNode("ELAKAY", null, "p", { fontSize: "10px", fontWeight: 800, color: C.orange, letterSpacing: "0.35em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "54px", fontWeight: 950, color: C.white, maxWidth: "760px", textAlign: "center" }),
      divider({ borderTop: `4px solid ${C.orange}`, width: "86px" }),
    ], { padding: "92px 48px 72px", background: `linear-gradient(135deg, ${C.navy} 0%, #1e293b 100%)`, alignItems: "center", textAlign: "center" }),
    section([repeatedSplit({ title: C.navy, text: C.gray, border: C.border, image: "https://placehold.co/520x340/e2e8f0/334155?text=Image" })], { background: C.bg, paddingTop: "36px" }),
  ]);
}

/* =============================================================================
   3 — Magazine vert
   ========================================================================== */
export function makeContentTemplateMagazine() {
  const C = { green: "#059669", bg: "#f0fdf4", white: "#ffffff", dark: "#064e3b", text: "#374151", border: "#bbf7d0" };
  return makePage("Contenu Magazine", [
    section([
      row([
        heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "34px", fontWeight: 900, color: C.dark, flex: "0 1 auto" }),
        divider({ flex: "1 1 160px", borderTop: "none", borderBottom: `1px solid ${C.border}`, marginBottom: "8px" }),
      ], { alignItems: "flex-end" }),
    ], { padding: "36px 48px 20px", background: C.white, borderBottom: `3px solid ${C.green}` }),
    section([
      {
        id: id(),
        type: "panel",
        repeatBind: rSections(),
        style: { background: C.white, borderRadius: "8px", border: `1px solid ${C.border}`, overflow: "hidden", display: "flex", flexDirection: "row", flexWrap: "wrap", boxShadow: "0 2px 8px rgba(5,150,105,0.06)" },
        children: [
          imageNode("https://placehold.co/320x260/d1fae5/059669?text=Image", { width: "300px", minHeight: "220px", flex: "1 1 260px", borderRadius: 0 }),
          videoNode({ width: "300px", minHeight: "220px", height: "260px", flex: "1 1 260px", borderRadius: 0 }),
          stack([
            heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "21px", fontWeight: 850, color: C.dark }),
            textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
            textNode("", "text.1", "p", { fontSize: "13px", color: "#6b7280" }),
          ], { flex: "2 1 320px", padding: "24px 28px", justifyContent: "center" }),
        ],
      },
    ], { background: C.bg, gap: "24px" }),
  ]);
}

/* =============================================================================
   4 — Studio sombre
   ========================================================================== */
export function makeContentTemplateSombre() {
  const C = { bg: "#0d1117", surface: "#161b22", cyan: "#00d4aa", text: "#c9d1d9", dim: "#8b949e", border: "#30363d", white: "#f0f6fc" };
  return makePage("Contenu Sombre", [
    section([
      textNode(elakayAutoText("h_7f952a67f5bad2", "CONTENT PAGE"), null, "p", { fontSize: "11px", fontWeight: 800, color: C.cyan, letterSpacing: "0.25em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "48px", fontWeight: 950, color: C.white }),
      divider({ borderTop: `2px solid ${C.border}` }),
    ], { background: C.bg, paddingBottom: "36px" }),
    section([
      panel([
        row([
          panel([], { width: "4px", height: "22px", padding: 0, background: C.cyan, border: "none", borderRadius: "2px", flex: "0 0 4px" }),
          heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "19px", fontWeight: 800, color: C.white }),
        ], { gap: "10px", alignItems: "center" }),
        textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.dim }),
        textNode("", "text.1", "p", { fontSize: "14px", color: C.dim }),
        imageNode("https://placehold.co/700x260/161b22/30363d?text=Image", { maxHeight: "240px", borderRadius: "6px" }),
        videoNode({ height: "260px", borderRadius: "6px" }),
      ], { background: C.surface, border: `1px solid ${C.border}` }, rSections()),
    ], { background: C.bg, paddingTop: "20px" }),
  ]);
}

/* =============================================================================
   5 — Timeline procedure
   ========================================================================== */
export function makeContentTemplateTimeline() {
  const C = { bg: "#fff7ed", ink: "#1f2937", text: "#57534e", accent: "#ea580c", line: "#fed7aa", paper: "#ffffff" };
  return makePage("Contenu Timeline", [
    section([
      textNode(elakayAutoText("h_0aa7c7339b06f1", "GUIDE ETAPES"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.22em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "46px", fontWeight: 950, color: C.ink, maxWidth: "760px" }),
    ], { background: C.bg, paddingBottom: "28px" }),
    section([
      row([
        panel([], { width: "3px", padding: 0, background: C.line, border: "none", borderRadius: "99px", alignSelf: "stretch", flex: "0 0 3px" }),
        panel([
          textNode("STEP", null, "p", { fontSize: "10px", fontWeight: 900, color: C.accent, letterSpacing: "0.18em" }),
          heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "22px", fontWeight: 850, color: C.ink }),
          textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "15px", color: C.text }),
          imageNode("https://placehold.co/700x260/fed7aa/ea580c?text=Image", { height: "220px", borderRadius: "6px" }),
          videoNode({ height: "240px", borderRadius: "6px" }),
        ], { flex: "1 1 320px", background: C.paper, border: `1px solid ${C.line}` }, rSections()),
      ], { gap: "18px", alignItems: "stretch" }),
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   6 — Legal clean
   ========================================================================== */
export function makeContentTemplateLegal() {
  const C = { bg: "#f8fafc", ink: "#111827", text: "#475569", accent: "#2563eb", paper: "#ffffff", border: "#cbd5e1" };
  return makePage("Contenu Legal", [
    section([
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "38px", fontWeight: 900, color: C.ink }),
      textNode(elakayAutoText("h_9312816d83c4cf", "Document structure"), null, "p", { fontSize: "13px", color: C.text }),
    ], { background: C.paper, borderBottom: `1px solid ${C.border}`, paddingBottom: "32px" }),
    section([
      panel([
        textNode("ARTICLE", null, "p", { fontSize: "10px", fontWeight: 900, color: C.accent, letterSpacing: "0.18em" }),
        heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "20px", fontWeight: 850, color: C.ink }),
        divider({ borderTop: `1px solid ${C.border}` }),
        textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
        textNode("", "text.1", "p", { fontSize: "14px", color: C.text }),
        imageNode("https://placehold.co/760x260/dbeafe/2563eb?text=Image", { height: "220px", borderRadius: "6px" }),
        videoNode({ height: "240px", borderRadius: "6px" }),
      ], { background: C.paper, border: `1px solid ${C.border}`, boxShadow: "0 1px 2px rgba(15,23,42,0.05)" }, rSections()),
    ], { background: C.bg, paddingTop: "34px" }),
  ]);
}

/* =============================================================================
   7 — FAQ cards
   ========================================================================== */
export function makeContentTemplateFaq() {
  const C = { bg: "#f5f3ff", ink: "#2e1065", text: "#5b21b6", accent: "#7c3aed", paper: "#ffffff", border: "#ddd6fe" };
  return makePage("Contenu FAQ", [
    section([
      textNode(elakayAutoText("h_de18d43dd72496", "QUESTIONS & REPONSES"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.18em", textAlign: "center" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "44px", fontWeight: 950, color: C.ink, textAlign: "center" }),
    ], { background: C.bg, alignItems: "center", paddingBottom: "24px" }),
    section([
      panel([
        row([
          panel([], { width: "32px", height: "32px", padding: 0, background: C.accent, border: "none", borderRadius: "50px", flex: "0 0 32px" }),
          stack([
            heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "19px", fontWeight: 850, color: C.ink }),
            textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
            imageNode("https://placehold.co/620x260/ddd6fe/7c3aed?text=Image", { height: "190px", borderRadius: "6px" }),
            videoNode({ height: "210px", borderRadius: "6px" }),
          ], { flex: "1 1 280px" }),
        ], { gap: "16px", alignItems: "flex-start" }),
      ], { background: C.paper, border: `1px solid ${C.border}`, boxShadow: "0 12px 24px rgba(124,58,237,0.08)" }, rSections()),
    ], { background: C.bg, paddingTop: "22px" }),
  ]);
}

/* =============================================================================
   8 — Proposal deck
   ========================================================================== */
export function makeContentTemplateProposal() {
  const C = { bg: "#eef2ff", ink: "#172554", text: "#334155", accent: "#4f46e5", paper: "#ffffff", border: "#c7d2fe" };
  return makePage("Contenu Proposition", [
    section([
      row([
        stack([
          textNode(elakayAutoText("h_ad5dc81b90ac13", "PROPOSITION"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.18em" }),
          heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "46px", fontWeight: 950, color: C.ink }),
        ], { flex: "2 1 380px" }),
        panel([
          textNode(elakayAutoText("h_e5a565b9c70f63", "Resume"), null, "p", { fontSize: "12px", fontWeight: 800, color: C.accent }),
          textNode(elakayAutoText("h_f10edafdee5eaa", "Contenu de la section..."), "contentPage.sections.0.text.0", "p", { fontSize: "14px", color: C.text }),
        ], { flex: "1 1 260px", background: C.paper, border: `1px solid ${C.border}` }),
      ], { alignItems: "stretch" }),
    ], { background: C.bg }),
    section([repeatedStatCard({ accent: C.accent, title: C.ink, text: C.text, border: C.border })], { background: C.bg, paddingTop: "8px" }),
  ]);
}

/* =============================================================================
   9 — Image dossier
   ========================================================================== */
export function makeContentTemplateImageDossier() {
  const C = { bg: "#fdf2f8", ink: "#831843", text: "#6b213d", accent: "#db2777", paper: "#ffffff", border: "#fbcfe8" };
  return makePage("Contenu Image Dossier", [
    section([
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "42px", fontWeight: 950, color: C.ink, textAlign: "center" }),
      textNode(elakayAutoText("h_f2bda2a70db57e", "Une mise en page visuelle pour pages riches en images."), null, "p", { fontSize: "14px", color: C.text, textAlign: "center" }),
    ], { background: C.bg, alignItems: "center", paddingBottom: "28px" }),
    section([
      panel([
        imageNode("https://placehold.co/760x420/fbcfe8/db2777?text=Image", { height: "300px", borderRadius: "8px" }),
        videoNode({ height: "320px", borderRadius: "8px" }),
        stack([
          heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "22px", fontWeight: 850, color: C.ink }),
          textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
        ], { paddingTop: "4px" }),
      ], { background: C.paper, border: `1px solid ${C.border}`, padding: "14px", boxShadow: "0 16px 28px rgba(219,39,119,0.08)" }, rSections()),
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   10 — Minimal monochrome
   ========================================================================== */
export function makeContentTemplateMinimal() {
  const C = { bg: "#ffffff", ink: "#111111", text: "#4b5563", line: "#d1d5db" };
  return makePage("Contenu Minimal", [
    section([
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "42px", fontWeight: 800, color: C.ink, maxWidth: "760px" }),
    ], { background: C.bg, paddingBottom: "20px" }),
    section([
      {
        id: id(),
        type: "stack",
        repeatBind: rSections(),
        style: { display: "flex", flexDirection: "column", gap: "10px", padding: "22px 0", borderTop: `1px solid ${C.line}` },
        children: [
          heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "21px", fontWeight: 800, color: C.ink }),
          textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "15px", color: C.text }),
          textNode("", "text.1", "p", { fontSize: "15px", color: C.text }),
          imageNode("https://placehold.co/760x260/f3f4f6/111111?text=Image", { height: "220px", borderRadius: "4px" }),
          videoNode({ height: "240px", borderRadius: "4px" }),
        ],
      },
    ], { background: C.bg, paddingTop: "0" }),
  ]);
}

/* =============================================================================
   11 — Split dossier
   ========================================================================== */
export function makeContentTemplateSplitDossier() {
  const C = { bg: "#f0f9ff", ink: "#082f49", text: "#475569", accent: "#0284c7", paper: "#ffffff", border: "#bae6fd" };
  return makePage("Contenu Split Dossier", [
    section([
      row([
        stack([
          textNode(elakayAutoText("h_7890bf01dff949", "DOSSIER"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.2em" }),
          heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "45px", fontWeight: 950, color: C.ink }),
        ], { flex: "1 1 360px", justifyContent: "center" }),
        imageNode("https://placehold.co/560x380/bae6fd/0284c7?text=Image", { flex: "1 1 320px", height: "320px", borderRadius: "8px" }, "contentPage.sections.0.imagePath"),
        videoNode({ flex: "1 1 320px", height: "320px", borderRadius: "8px" }, "contentPage.sections.0.videoPath"),
      ], { alignItems: "stretch" }),
    ], { background: C.bg }),
    section([repeatedSplit({ title: C.ink, text: C.text, border: C.border, image: "https://placehold.co/520x340/e0f2fe/0284c7?text=Image", imageWidth: "38%" })], { background: C.paper, paddingTop: "26px" }),
  ]);
}

/* =============================================================================
   12 — Checklist guide
   ========================================================================== */
export function makeContentTemplateChecklist() {
  const C = { bg: "#ecfdf5", ink: "#064e3b", text: "#375a4a", accent: "#10b981", paper: "#ffffff", border: "#a7f3d0" };
  return makePage("Contenu Checklist", [
    section([
      textNode(elakayAutoText("h_8b0fd77f30f2a0", "CHECKLIST"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.2em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "44px", fontWeight: 950, color: C.ink }),
    ], { background: C.bg, paddingBottom: "24px" }),
    section([
      panel([
        row([
          panel([], { width: "24px", height: "24px", padding: 0, background: C.accent, border: "none", borderRadius: "50px", flex: "0 0 24px" }),
          stack([
            heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h3", { fontSize: "20px", fontWeight: 850, color: C.ink }),
            textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
            textNode("", "list.0", "p", { fontSize: "13px", fontWeight: 700, color: C.accent }),
            imageNode("https://placehold.co/620x260/a7f3d0/10b981?text=Image", { height: "190px", borderRadius: "6px" }),
            videoNode({ height: "210px", borderRadius: "6px" }),
          ], { flex: "1 1 300px" }),
        ], { gap: "14px", alignItems: "flex-start" }),
      ], { background: C.paper, border: `1px solid ${C.border}` }, rSections()),
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   13 — Bento sections
   ========================================================================== */
export function makeContentTemplateBento() {
  const C = { bg: "#f8fafc", ink: "#0f172a", text: "#475569", accent: "#0d9488", paper: "#ffffff", border: "#ccfbf1" };
  return makePage("Contenu Bento", [
    section([
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "46px", fontWeight: 950, color: C.ink, textAlign: "center" }),
      textNode(elakayAutoText("h_52a9430fb9f7b8", "Sections en blocs compacts pour lecture rapide."), null, "p", { fontSize: "14px", color: C.text, textAlign: "center" }),
    ], { background: C.bg, alignItems: "center", paddingBottom: "22px" }),
    section([
      {
        id: id(),
        type: "grid",
        style: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "16px" },
        options: { columns: 3 },
        children: [
          panel([
            heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h3", { fontSize: "20px", fontWeight: 850, color: C.ink }),
            textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
            imageNode("https://placehold.co/480x260/ccfbf1/0d9488?text=Image", { height: "180px", borderRadius: "6px" }),
            videoNode({ height: "200px", borderRadius: "6px" }),
          ], { border: `1px solid ${C.border}`, boxShadow: "0 8px 22px rgba(15,23,42,0.06)" }, rSections()),
        ],
      },
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   14 — Contract signature
   ========================================================================== */
export function makeContentTemplateContract() {
  const C = { bg: "#fefce8", ink: "#422006", text: "#713f12", accent: "#ca8a04", paper: "#ffffff", border: "#fde68a" };
  return makePage("Contenu Contrat", [
    section([
      row([
        stack([
          textNode(elakayAutoText("h_6b0da53ec47a7a", "ACCORD"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.2em" }),
          heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "42px", fontWeight: 950, color: C.ink }),
        ], { flex: "2 1 360px" }),
        panel([
          textNode(elakayAutoText("h_aa3b75f005764f", "Signature"), null, "p", { fontSize: "12px", fontWeight: 900, color: C.accent }),
          divider({ borderTop: `1px solid ${C.border}`, marginTop: "24px" }),
        ], { flex: "1 1 240px", background: C.paper, border: `1px solid ${C.border}` }),
      ], { alignItems: "stretch" }),
    ], { background: C.bg }),
    section([
      panel([
        heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "20px", fontWeight: 850, color: C.ink }),
        textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
        textNode("", "text.1", "p", { fontSize: "14px", color: C.text }),
        imageNode("https://placehold.co/760x260/fde68a/ca8a04?text=Image", { height: "220px", borderRadius: "6px" }),
        videoNode({ height: "240px", borderRadius: "6px" }),
      ], { background: C.paper, border: `1px solid ${C.border}` }, rSections()),
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   15 — Video learning
   ========================================================================== */
export function makeContentTemplateVideoGuide() {
  const C = { bg: "#111827", surface: "#1f2937", ink: "#f9fafb", text: "#d1d5db", accent: "#38bdf8", border: "#374151" };
  return makePage("Contenu Video Guide", [
    section([
      textNode(elakayAutoText("h_7dbcb03849c140", "VIDEO GUIDE"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.22em" }),
      heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "46px", fontWeight: 950, color: C.ink, maxWidth: "780px" }),
    ], { background: C.bg, paddingBottom: "24px" }),
    section([
      panel([
        imageNode("https://placehold.co/860x430/1f2937/38bdf8?text=Media", { height: "330px", borderRadius: "8px" }),
        videoNode({ height: "340px", borderRadius: "8px" }),
        heading(elakayAutoText("h_eb97899aedcd56", "Titre"), "title", "h2", { fontSize: "23px", fontWeight: 850, color: C.ink }),
        textNode(elakayAutoText("h_f092a0a855c22f", "Texte..."), "text.0", "p", { fontSize: "14px", color: C.text }),
      ], { background: C.surface, border: `1px solid ${C.border}` }, rSections()),
    ], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   16 — Portal landing
   ========================================================================== */
export function makeContentTemplatePortal() {
  const C = { bg: "#f7fee7", ink: "#1a2e05", text: "#3f6212", accent: "#65a30d", paper: "#ffffff", border: "#d9f99d" };
  return makePage("Contenu Portal", [
    section([
      row([
        panel([
          textNode(elakayAutoText("h_4ef21cc746c9fb", "PORTAL"), null, "p", { fontSize: "11px", fontWeight: 900, color: C.accent, letterSpacing: "0.2em" }),
          heading(elakayAutoText("h_79cc53f1db918d", "Titre de la page"), "contentPage.pageTitle", "h1", { fontSize: "44px", fontWeight: 950, color: C.ink }),
          textNode(elakayAutoText("h_a4a8931ba9d7f5", "Une page d'accueil claire pour orienter l'utilisateur."), null, "p", { fontSize: "15px", color: C.text }),
        ], { flex: "1 1 360px", border: `1px solid ${C.border}`, boxShadow: "0 16px 34px rgba(101,163,13,0.10)" }),
        imageNode("https://placehold.co/560x420/d9f99d/65a30d?text=Portal", { flex: "1 1 320px", height: "360px", borderRadius: "8px" }, "contentPage.sections.0.imagePath"),
        videoNode({ flex: "1 1 320px", height: "360px", borderRadius: "8px" }, "contentPage.sections.0.videoPath"),
      ], { alignItems: "stretch" }),
    ], { background: C.bg }),
    section([repeatedArticleCard({ title: C.ink, text: C.text, bg: C.paper, border: C.border, image: "https://placehold.co/760x320/ecfccb/65a30d?text=Image" })], { background: C.bg, paddingTop: "18px" }),
  ]);
}

/* =============================================================================
   Catalogue des templates contentPage
   ========================================================================== */
export const CONTENT_TEMPLATES = [
  {
    key: "contentClassique",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_5429e6989a6c19", "Contenu Classique"); },
    get description() { return elakayAutoText("h_504af0bc731ce1", "Blanc/violet - hero titre + sections cartes empilees avec image."); },
    palette: ["#f8fafc", "#6d28d9", "#ffffff"],
    make: makeContentTemplateClassique,
  },
  {
    key: "contentHero",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_3b9bd0fd24fd7e", "Contenu Hero"); },
    get description() { return elakayAutoText("h_d424682e9c8b29", "Marine/orange - grand hero sombre + sections alternees image/texte."); },
    palette: ["#0f172a", "#f97316", "#f8fafc"],
    make: makeContentTemplateHero,
  },
  {
    key: "contentMagazine",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_11349d04f7f981", "Contenu Magazine"); },
    get description() { return elakayAutoText("h_6ad241a5083f73", "Vert/blanc - style editorial avec cartes horizontales image/texte."); },
    palette: ["#f0fdf4", "#059669", "#064e3b"],
    make: makeContentTemplateMagazine,
  },
  {
    key: "contentSombre",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_1c85d4f996c3e2", "Contenu Sombre"); },
    get description() { return elakayAutoText("h_ba369e39e6043b", "Noir/cyan - theme studio sombre avec surfaces techniques."); },
    palette: ["#0d1117", "#00d4aa", "#161b22"],
    make: makeContentTemplateSombre,
  },
  {
    key: "contentTimeline",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_4ef52ef0d94ed6", "Contenu Timeline"); },
    get description() { return elakayAutoText("h_f4df23b2d15656", "Orange clair - lecture en etapes verticales pour guides et procedures."); },
    palette: ["#fff7ed", "#ea580c", "#fed7aa"],
    make: makeContentTemplateTimeline,
  },
  {
    key: "contentLegal",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_04751391fb929a", "Contenu Legal"); },
    get description() { return elakayAutoText("h_478308a35d1db5", "Bleu sobre - structure documentaire pour conditions, politiques et accords."); },
    palette: ["#f8fafc", "#2563eb", "#cbd5e1"],
    make: makeContentTemplateLegal,
  },
  {
    key: "contentFaq",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_390b375a364462", "Contenu FAQ"); },
    get description() { return elakayAutoText("h_7df93a127f2668", "Violet doux - blocs question/reponse pour aide et explications rapides."); },
    palette: ["#f5f3ff", "#7c3aed", "#ddd6fe"],
    make: makeContentTemplateFaq,
  },
  {
    key: "contentProposal",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_bf4a4021afbd50", "Contenu Proposition"); },
    get description() { return elakayAutoText("h_8c733198bf74ee", "Indigo - presentation type dossier/proposition avec resume et cartes."); },
    palette: ["#eef2ff", "#4f46e5", "#172554"],
    make: makeContentTemplateProposal,
  },
  {
    key: "contentImageDossier",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_d875320b1038bc", "Contenu Image Dossier"); },
    get description() { return elakayAutoText("h_d22097a9182658", "Rose editorial - mise en avant visuelle pour pages riches en medias."); },
    palette: ["#fdf2f8", "#db2777", "#fbcfe8"],
    make: makeContentTemplateImageDossier,
  },
  {
    key: "contentMinimal",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_a7b68dbb2a14fb", "Contenu Minimal"); },
    get description() { return elakayAutoText("h_d78ea85c27278b", "Monochrome - lecture rapide, typographie simple, zero decoration."); },
    palette: ["#ffffff", "#111111", "#d1d5db"],
    make: makeContentTemplateMinimal,
  },
  {
    key: "contentSplitDossier",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_22c37f34826bcc", "Contenu Split Dossier"); },
    get description() { return elakayAutoText("h_4b3867303a48a1", "Bleu ciel - hero en deux colonnes puis sections image/texte."); },
    palette: ["#f0f9ff", "#0284c7", "#bae6fd"],
    make: makeContentTemplateSplitDossier,
  },
  {
    key: "contentChecklist",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_480d5d4cab0d48", "Contenu Checklist"); },
    get description() { return elakayAutoText("h_6689d32c5925f8", "Vert menthe - sections actionnables avec repere de validation."); },
    palette: ["#ecfdf5", "#10b981", "#a7f3d0"],
    make: makeContentTemplateChecklist,
  },
  {
    key: "contentBento",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_e55b0b9158c621", "Contenu Bento"); },
    get description() { return elakayAutoText("h_676f561a2aa60b", "Teal - grille compacte de blocs pour comparer plusieurs sections."); },
    palette: ["#f8fafc", "#0d9488", "#ccfbf1"],
    make: makeContentTemplateBento,
  },
  {
    key: "contentContract",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_7995f60c3d95be", "Contenu Contrat"); },
    get description() { return elakayAutoText("h_9fa32134d7c1cb", "Jaune papier - structure accord avec zone signature et articles."); },
    palette: ["#fefce8", "#ca8a04", "#fde68a"],
    make: makeContentTemplateContract,
  },
  {
    key: "contentVideoGuide",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_0df79a328fd4d5", "Contenu Video Guide"); },
    get description() { return elakayAutoText("h_24323bf587a41d", "Sombre media - pages de tutoriel avec grand bloc visuel."); },
    palette: ["#111827", "#38bdf8", "#1f2937"],
    make: makeContentTemplateVideoGuide,
  },
  {
    key: "contentPortal",
    pageMode: "contentPage",
    get label() { return elakayAutoText("h_baa2d2349a7f42", "Contenu Portal"); },
    get description() { return elakayAutoText("h_f84f12d7995c11", "Lime - page d'orientation avec hero carte et sections detaillees."); },
    palette: ["#f7fee7", "#65a30d", "#d9f99d"],
    make: makeContentTemplatePortal,
  },
];

// =============================================================================
// "Classic Content" preload mission -- ONE stable key for the System Template
// used as the default starter HTML for a Content Page's Data Source that has
// never been customized (task spec §6/§7/§8: "Ne hardcode pas une seconde
// copie du HTML... Utiliser la vraie définition... Ne cherche jamais
// uniquement title === 'Classic Content'"). This is genuinely the SAME
// "Contenu Classique" entry Family already sees/picks in the System
// Templates dropdown (`contentTemplate.contentClassique`, the historical
// default the fix-400 mission's own live tests already used) -- exported as
// a named constant here so no call site has to re-type the literal string or
// search CONTENT_TEMPLATES by label.
export const CLASSIC_CONTENT_TEMPLATE_KEY = "contentClassique";

export const getClassicContentDoc = () => {
  const tpl = CONTENT_TEMPLATES.find((t) => t.key === CLASSIC_CONTENT_TEMPLATE_KEY);
  return tpl ? tpl.make() : { meta: {}, pageMode: "contentPage", pages: [{ id: "p1", children: [] }] };
};

// The ONE resolver from a `getPageTemplateOverride`-shaped result --
// { pagebuilderDoc, templateKey, group } -- to the actual JSON block tree to
// render/generate HTML from. Real DB customization (a template Family has
// itself edited via Edit HTML Code, priority ON) always wins over the
// hardcoded registry source; a `contentTemplate.*` key with NO customization
// falls back to this SAME registry's `tpl.make()` -- exactly
// useContentPageOverride.js's own public-render fallback, reused here
// instead of re-implemented (task spec §7/§30: "Ne duplique pas cette
// logique dans plusieurs handlers/fichiers"). Returns `null` when nothing
// resolves (e.g. a `contentPageTemplate.*` Family template with no source of
// its own yet) -- callers decide their own further fallback (Classic
// Content, original page, ...).
export const resolveTemplateJsonDoc = ({ pagebuilderDoc, templateKey, group } = {}) => {
  const doc = pagebuilderDoc?.doc;
  if (doc && Array.isArray(doc.pages)) return doc;
  if (group === "contentTemplate" && typeof templateKey === "string" && templateKey.startsWith("contentTemplate.")) {
    const key = templateKey.slice("contentTemplate.".length);
    const tpl = CONTENT_TEMPLATES.find((t) => t.key === key);
    if (tpl) return tpl.make();
  }
  return null;
};
